import React, { useContext, useEffect, useMemo, useState } from 'react';
import { CONFIG } from 'config/config';
import routes from 'config/config/routes';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import {
	NavigationWrapper,
	PickerHOC,
	TargetsWrapper,
	Text,
	UiWrapper,
	UserWrapper,
	View,
} from '@coinscrap/webapp-core';

import { Wrapper } from 'layout/components/Body/Wrapper';
import Footer from 'layout/components/Footer/Footer';
import Header from 'layout/components/Header/Header';

import BackNextButton from 'components/BackNextButton/BackNextButton';
import LoadingScreen from 'components/LoadingScreen/LoadingScreen';
import { MainModal } from 'components/MainModal/MainModal';

import { useGetError } from 'hooks/useGetError';
import { useInitialScrollToTop } from 'hooks/useInitialScrollToTop';

import edit from 'assets/images/svgStatic/edit.svg';
import spinner from 'assets/images/svgStatic/spinner.svg';

import { getCurrency } from 'helpers/currency';

import { DebugContext } from '../../App';
import { AgentContext } from '../../contexts/agent';
import { useCoinscrapPageView } from '../../hooks/useCoinscrapPageView';
import { usePreemission } from './hooks/usePreemission';
import { usePreemissionError } from './hooks/usePreemissionError';
import * as S from './styles';

export const ActivateProduct = PickerHOC(
	{
		target: {
			sourceWrapper: TargetsWrapper,
			sourcePath: 'targets',
			arrayItemMatcher: {
				type: 'param',
				paramName: 'targetId',
				itemPropertyPath: 'id',
			},
		},
		user: {
			sourceWrapper: UserWrapper,
			sourcePath: 'user',
		},
	},
	LoadingScreen,
)(() => {
	const { agent } = useContext(AgentContext);
	const { useHeader, useFooter, useLoading, openModal } = UiWrapper.use();
	const { goBack, navigateTo } = NavigationWrapper.use();
	const pickerValues = PickerHOC.use();
	const { target, user } = pickerValues;
	const { t } = useTranslation();
	const isDebug = React.useContext(DebugContext);

	const [loading, setLoading] = useState(false);
	const [queryParams, setQueryParams] = useState({ mode: 'edit' });

	useFooter(<Footer />, []);
	useHeader(<Header />, []);
	useLoading(loading, { loadingMessage: loading });
	useInitialScrollToTop();
	useCoinscrapPageView('ActivateProduct');
	const getError = useGetError();

	// TARGET CREATION DATA
	const creationData = useMemo(
		() => target?.targetProperties?.find((x) => x.name === 'creationData')?.value,
		[target],
	);

	// ESTADO
	const targetStatus = useMemo(() => target.status, [target]);
	const envelopeId = useMemo(() => target?.signData?.envelopeId, [target]);

	const [activateStep, setActivateStep] = useState(1);
	const [trigger, setTrigger] = useState(0);

	// START ACTIVATION / PREEMISSION
	const preemission = usePreemission(
		target.status,
		activateStep,
		target.id,
		trigger,
		setTrigger,
		usePreemissionError,
		agent
	);

	// NAVIGATION
	const next = async () => {
		switch (activateStep) {
			case 1:
				setActivateStep(2);
				break;
			case 2:
				try {
					CONFIG.routing.activateProduct.next(navigateTo, envelopeId);
					// navigateTo(routes.signatureRead);
				} catch (e) {
					setLoading(undefined);
					openModal(() => <MainModal type="error" text={getError(e)} />, {
						disableClickAway: true,
						closeButton: true,
						modalClass: isMobile ? 'modal-xl' : 'modal-main',
						key: 'modal-ActivateProduct-Next-KO',
					});
				}
				break;
			default:
				setLoading(undefined);
				openModal(
					() => (
						<MainModal
							type="error"
							text={getError(
								new Error(`activateStep no válido: activateStep ${activateStep}`),
							)}
						/>
					),
					{
						disableClickAway: true,
						closeButton: true,
						modalClass: isMobile ? 'modal-xl' : 'modal-main',
						key: 'modal-ActivateProduct-defaultKO',
					},
				);
				break;
		}
	};

	const isDisabled = () => {
		if (activateStep === 1) {
			return false;
		}
		return targetStatus !== 'ACTIVATING' || !envelopeId;
	};

	useEffect(() => {
		console.log('📌 user', user);
		console.log('📌 creationData', creationData);
		console.log('📌 target.status', target.status);
		return () => { };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (activateStep) {
			console.log('👉 activateStep', activateStep);
		}
		return () => { };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activateStep]);

	return (
		<Wrapper
			showTopSection
			step={5}
			title={
				activateStep === 1 ? 'Resumen' : '¡Ya casi estamos! Tu póliza se está procesando'
			}
			subtitle={
				activateStep === 1
					? 'Por favor, verifica que los datos que te mostramos a continuación son correctos antes de seguir con el proceso de contratación.'
					: ''
			}
		>
			<View innerWrapperSt>
				{
					{
						// CONFIRMACIÓN DE DATOS
						1: (
							<>
								{/* PRIMA Y CAPITAL */}
								<View
									rowSt
									justifyBetweenSt
									alignItemsCenterSt
									style={{ marginTop: 12 }}
								>
									<Text headingTertiarySt leftSt>
										Tu seguro de vida tiene una prima de{' '}
										{getCurrency(
											creationData?.selectedPack?.totalPremiumAmount,
											2,
										)}{' '}
										al año y asegura un capital de{' '}
										{getCurrency(creationData?.price, 0)}
									</Text>
									<S.IconContainer
										onClick={() =>
											navigateTo(routes.simulation, {
												queryParams: { editMode: true },
											})
										}
									>
										<img src={edit} alt={'edit'} style={{ width: '100%' }} />
									</S.IconContainer>
								</View>

								{/* PERIODICIDAD */}
								{/* <Text
									headingTertiarySt
									style={{ marginTop: 8 }}
									fullWidthSt
									leftSt
								>
									Periodicidad:{' '}
									{creationData?.selectedPack?.paymentTypeDescription}
								</Text> */}

								{/* BENEFICIARIOS */}
								<View
									rowSt
									justifyBetweenSt
									alignItemsCenterSt
									style={{ marginTop: 8 }}
								>
									{creationData?.details?.beneficiaryType === 'generico' ? (
										<Text headingTertiarySt leftSt>
											Beneficiario: Herederos legales
										</Text>
									) : creationData?.details?.beneficiaryType === 'expreso' ? (
										<Text headingTertiarySt leftSt>
											Beneficiarios:{' '}
											{creationData?.details?.beneficiaries.map(
												(beneficiary, index, array) => {
													if (index === 0) {
														return `${beneficiary?.name} ${beneficiary?.surname}`;
													} else if (
														index > 0 &&
														index === array.length - 1
													) {
														return ` y ${beneficiary?.name} ${beneficiary?.surname}`;
													} else {
														return `, ${beneficiary?.name} ${beneficiary?.surname}`;
													}
												},
											)}
										</Text>
									) : (
										<Text headingTertiarySt leftSt>
											Beneficiario: Herederos legales
										</Text>
									)}
									<S.IconContainer
										onClick={() =>
											navigateTo(routes.clientData, {
												queryParams: { internalStep: 4, editMode: true },
											})
										}
									>
										<img src={edit} alt={'edit'} style={{ width: '100%' }} />
									</S.IconContainer>
								</View>

								{/* FECHAS */}
								<Text headingTertiarySt style={{ marginTop: 8 }} fullWidthSt leftSt>
									Fecha de inicio:{' '}
									{creationData?.policyDates?.policyEffectiveDate}
								</Text>
								<Text headingTertiarySt style={{ marginTop: 8 }} fullWidthSt leftSt>
									Fecha de vencimiento:{' '}
									{creationData?.policyDates?.policyExpirationDate}
								</Text>
							</>
						),
						// GENERACIÓN DE DOCUMENTOS
						2: (
							<>
								<View>
									<Text
										headingTertiarySt
										style={{ marginTop: 12, marginBottom: 30 }}
										fullWidthSt
										leftSt
									>
										Estamos trabajando en finalizar el contrato de tu seguro. El
										proceso puede tardar hasta 30 segundos, no cierres la
										ventana.
									</Text>
									{targetStatus !== 'ACTIVATING' && (
										<S.SpinnerContainer>
											<img
												src={spinner}
												alt={'spinner'}
												style={{ width: '100%' }}
											/>
										</S.SpinnerContainer>
									)}
								</View>
							</>
						),
					}[activateStep]
				}
			</View>

			<BackNextButton
				isBackShown={activateStep === 1 ? true : false}
				onClickBack={() => navigateTo(routes.bankAccount)}
				onClickNext={next}
				nextText={activateStep === 1 ? 'Siguiente' : 'Firmar'}
				isNextDisabled={isDisabled()}
			/>
		</Wrapper>
	);
});
